import type { BadgeTone } from '@/components/ui'
import type { AnexoUpload } from '@/types/arquivo'

export const FERIAS_STATUS = ['PENDENTE', 'APROVADA', 'EM_GOZO', 'CONCLUIDA', 'CANCELADA'] as const

export type FeriasStatus = (typeof FERIAS_STATUS)[number]

export const FERIAS_STATUS_LABEL: Record<FeriasStatus, string> = {
  PENDENTE: 'Pendente',
  APROVADA: 'Aprovada',
  EM_GOZO: 'Em gozo',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
}

/** Tom visual de cada status — fonte única para badges e barras do calendário. */
export const FERIAS_STATUS_TONE: Record<FeriasStatus, BadgeTone> = {
  PENDENTE: 'attention',
  APROVADA: 'progress',
  EM_GOZO: 'success',
  CONCLUIDA: 'neutral',
  CANCELADA: 'critical',
}

/**
 * Datas em `yyyy-MM-dd`. O período aquisitivo e o limite concessivo são calculados pela API a
 * partir do gozo. Registros antigos podem vir sem gozo (`null`).
 */
export interface Ferias {
  id: string
  colaboradorId: string
  dataInicioPeriodoAquisitivo: string
  dataFimPeriodoAquisitivo: string
  /** Calculada pela API. */
  dataLimiteConcessiva: string
  dataInicioGozo: string | null
  dataFimGozo: string | null
  diasGozo: number | null
  diasAbonoPecuniario: number
  /** Dias ainda disponíveis no período aquisitivo deste registro. */
  diasPendentesPeriodo: number
  status: FeriasStatus
  observacao: string | null
  dataCadastro: string
  atualizadoEm: string
  /** Documento do aviso de férias (`null` = sem documento). Abre em `/armazenamento/{id}`. */
  avisoArquivoId: string | null
  avisoNomeArquivo: string | null
  avisoExtensaoArquivo: string | null
  avisoVisivel: boolean
  /** `yyyy-MM-dd`. Vazia: o aviso aparece para o colaborador assim que `avisoVisivel` ligar. */
  avisoDataLiberacao: string | null
  avisoNotificadoEm: string | null
}

/**
 * Body de `PUT /Colaborador/{id}/ferias/{feriasId}/aviso`. Sem `arquivo`, só altera a exibição;
 * com ele, substitui o documento anterior (409 se não houver documento e não vier arquivo).
 */
export interface AvisoFeriasPayload {
  arquivo?: AnexoUpload
  visivel: boolean
  dataLiberacao: string | null
}

export type AvisoFeriasSituacao = 'SEM_AVISO' | 'OCULTO' | 'AGENDADO' | 'LIBERADO'

/** Situação do aviso para o colaborador, a partir dos campos `aviso*` e da data de hoje. */
export function situacaoAvisoFerias(
  ferias: Pick<Ferias, 'avisoArquivoId' | 'avisoVisivel' | 'avisoDataLiberacao'>,
  hoje: string,
): AvisoFeriasSituacao {
  if (!ferias.avisoArquivoId) return 'SEM_AVISO'
  if (!ferias.avisoVisivel) return 'OCULTO'
  const liberacao = ferias.avisoDataLiberacao?.slice(0, 10)
  return liberacao && liberacao > hoje ? 'AGENDADO' : 'LIBERADO'
}

/**
 * Body do POST. Período aquisitivo, `status`, `diasGozo` e `dataLimiteConcessiva` são definidos
 * pela API. O gozo tem de 5 a 30 dias corridos.
 */
export interface FeriasPayload {
  dataInicioGozo: string
  dataFimGozo: string
  diasAbonoPecuniario: number
  observacao: string | null
}

/** Body do PUT: substitui todos os campos — reenvie os que não mudaram. */
export interface FeriasUpdatePayload extends FeriasPayload {
  status: FeriasStatus
}

export type FeriasSaldoSituacao = 'EM_AQUISICAO' | 'DISPONIVEL' | 'VENCIDA' | 'QUITADA'

export const FERIAS_SALDO_SITUACAO_LABEL: Record<FeriasSaldoSituacao, string> = {
  EM_AQUISICAO: 'Em aquisição',
  DISPONIVEL: 'Disponível',
  VENCIDA: 'Vencida',
  QUITADA: 'Quitada',
}

/**
 * Saldo de um período aquisitivo (`GET /Colaborador/{id}/ferias/saldo`). Gozados e abono somam só
 * APROVADA/EM_GOZO/CONCLUIDA; `diasSolicitados` soma os PENDENTE, que ainda não descontam do saldo.
 */
export interface FeriasSaldo {
  dataInicioPeriodoAquisitivo: string
  dataFimPeriodoAquisitivo: string
  dataLimiteConcessiva: string
  diasDireito: number
  diasGozados: number
  diasAbonoPecuniario: number
  diasPendentes: number
  diasSolicitados: number
  situacao: FeriasSaldoSituacao
}
